// src/simulation/circuitBreaker.ts
import { BackendUnavailableError } from '../rest/errors.js';
/**
 * CircuitBreaker protects the backend from overload and automatically falls back
 * to simulated data (demo mode) when the backend is unavailable.
 *
 * State transitions:
 * ```
 * Closed ──(threshold failures)──► Open ──(openTimeout)──► HalfOpen
 *   ▲                                                          │
 *   └──────────────(success)──────────────────────────────────┘
 *                                  Open ◄──(failure)──────────┘
 * ```
 *
 * @example
 * const cb = new CircuitBreaker({ failureThreshold: 3, openTimeout: 30_000, requestTimeout: 5_000 });
 * const { data, simulated } = await cb.call(() => fetch('/api'), () => loadDemo());
 * if (simulated) showDemoBanner();
 */
export class CircuitBreaker {
    _failureCount = 0;
    _state = 'Closed';
    _nextAttempt = 0;
    _options;
    /**
     * @param {CircuitBreakerOptions} options - Thresholds and timeouts.
     */
    constructor(options) {
        this._options = options;
    }
    /**
     * Executes the primary function, falling back to the simulation only when the
     * circuit is **Open**.
     *
     * - **Closed** : Calls `fn`. On failure the error is re-thrown and the failure
     *   counter is incremented. The circuit opens once the threshold is reached.
     * - **Open**   : Calls `fallback` immediately. Returns `{ data, simulated: true }`.
     * - **HalfOpen**: Calls `fn`. On success → Closed. On failure → Open.
     *
     * @template T
     * @param {() => Promise<T>} fn - Primary async function (real backend call).
     * @param {() => Promise<T>} fallback - Fallback async function (simulation).
     * @returns {Promise<{ data: T; simulated: boolean }>}
     * @throws Re-throws backend errors when the circuit is Closed or HalfOpen.
     */
    async call(fn, fallback) {
        // Mode démo : AUCUN appel backend — fallback (données statiques) directement.
        // Les démos travaillent hors-ligne (fixtures annotées) ; le backend n'est
        // jamais sollicité (objectif : démos non autorisées sur le backend).
        if (this._options.demoMode) {
            const data = await fallback();
            return { data, simulated: true };
        }
        const now = Date.now();
        // Open state (prod) : ne JAMAIS fabriquer de données → erreur typée.
        if (this._state === 'Open' && now < this._nextAttempt) {
            throw new BackendUnavailableError();
        }
        // Open state: timeout elapsed → move to HalfOpen for one probe
        if (this._state === 'Open' && now >= this._nextAttempt) {
            this._state = 'HalfOpen';
        }
        // Closed or HalfOpen: attempt the real call
        try {
            const result = await this._withTimeout(fn, this._options.requestTimeout);
            this._reset();
            return { data: result, simulated: false };
        }
        catch (err) {
            this._recordFailure();
            throw err; // Let the caller handle the error
        }
    }
    /** True si le breaker est en mode démonstration (aucun appel backend). */
    isDemoMode() {
        return this._options.demoMode === true;
    }
    /**
     * Applies an optional timeout wrapper around a promise-returning function.
     *
     * @template T
     * @param {() => Promise<T>} promiseFn
     * @param {number} [timeoutMs]
     * @returns {Promise<T>}
     */
    _withTimeout(promiseFn, timeoutMs) {
        if (!timeoutMs)
            return promiseFn();
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('Request timeout')), timeoutMs);
            promiseFn()
                .then((val) => { clearTimeout(timer); resolve(val); })
                .catch((e) => { clearTimeout(timer); reject(e); });
        });
    }
    /** Increments failure count and trips the circuit if threshold is reached. */
    _recordFailure() {
        this._failureCount += 1;
        if (this._failureCount >= this._options.failureThreshold) {
            this._trip();
        }
    }
    /** Resets the circuit to Closed and clears the failure counter. */
    _reset() {
        this._failureCount = 0;
        this._state = 'Closed';
    }
    /** Opens the circuit and schedules the next HalfOpen attempt. */
    _trip() {
        this._state = 'Open';
        this._nextAttempt = Date.now() + this._options.openTimeout;
    }
    /**
     * Returns the current state of the circuit breaker.
     * @returns {CircuitState}
     */
    getState() {
        return this._state;
    }
}
//# sourceMappingURL=circuitBreaker.js.map